import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import Modal from './Common/Modal';

const INQUIRY_TYPES = [
  { value: 'admission', label: 'Admission', icon: 'fa-user-graduate', color: 'bg-blue-100 text-blue-800' },
  { value: 'general',   label: 'General',   icon: 'fa-info-circle',    color: 'bg-gray-100 text-gray-700' },
  { value: 'fee',       label: 'Fee',       icon: 'fa-credit-card',    color: 'bg-green-100 text-green-800' },
  { value: 'transfer',  label: 'Transfer',  icon: 'fa-exchange-alt',   color: 'bg-purple-100 text-purple-800' },
  { value: 'other',     label: 'Other',     icon: 'fa-ellipsis-h',     color: 'bg-yellow-100 text-yellow-800' },
];

const STATUS_META = {
  open:      { label: 'Open',      color: 'bg-yellow-100 text-yellow-800' },
  follow_up: { label: 'Follow Up', color: 'bg-blue-100 text-blue-800' },
  converted: { label: 'Converted', color: 'bg-green-100 text-green-800' },
  closed:    { label: 'Closed',    color: 'bg-gray-100 text-gray-700' },
};

const EMPTY_FORM = {
  visitor_name: '', phone: '', email: '',
  inquiry_type: 'admission', child_name: '',
  child_dob: '', class_interested: '',
  notes: '', status: 'open', follow_up_date: '',
};

export default function AdmissionInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/reception/inquiries');
      setInquiries(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = inquiries;
    if (statusFilter !== 'all') list = list.filter(i => i.status === statusFilter);
    if (typeFilter !== 'all') list = list.filter(i => i.inquiry_type === typeFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i => [i.visitor_name, i.phone, i.email, i.child_name].join(' ').toLowerCase().includes(q));
    }
    return list;
  }, [inquiries, statusFilter, typeFilter, search]);

  const stats = useMemo(() => ({
    total: inquiries.length,
    open: inquiries.filter(i => i.status === 'open').length,
    followUp: inquiries.filter(i => i.status === 'follow_up').length,
    converted: inquiries.filter(i => i.status === 'converted').length,
  }), [inquiries]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      visitor_name: row.visitor_name || '',
      phone: row.phone || '',
      email: row.email || '',
      inquiry_type: row.inquiry_type || 'admission',
      child_name: row.child_name || '',
      child_dob: row.child_dob || '',
      class_interested: row.class_interested || '',
      notes: row.notes || '',
      status: row.status || 'open',
      follow_up_date: row.follow_up_date || '',
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.visitor_name.trim()) {
      alert('Visitor name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/reception/inquiries/${editing.id}`, form);
      } else {
        await api.post('/reception/inquiries', form);
      }
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this inquiry record?')) return;
    try {
      await api.delete(`/reception/inquiries/${id}`);
      load();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <i className="fas fa-clipboard-list text-blue-500"></i>
            Admission & Inquiries
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Record every walk-in, phone call, or admission request.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50">
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} mr-1`}></i> Refresh
          </button>
          <button onClick={openCreate} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm">
            <i className="fas fa-plus mr-1"></i> New Inquiry
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-4 rounded shadow border-l-4 border-gray-700">
          <p className="text-xs text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-yellow-500">
          <p className="text-xs text-gray-500">Open</p>
          <p className="text-2xl font-bold text-yellow-700">{stats.open}</p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-blue-500">
          <p className="text-xs text-gray-500">Follow Up</p>
          <p className="text-2xl font-bold text-blue-700">{stats.followUp}</p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-green-500">
          <p className="text-xs text-gray-500">Converted</p>
          <p className="text-2xl font-bold text-green-700">{stats.converted}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        <select className="p-2 border rounded text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="p-2 border rounded text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          {INQUIRY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <input
          type="text"
          placeholder="Search by visitor, phone, child name..."
          className="flex-1 min-w-[200px] p-2 border rounded text-sm"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span className="text-sm text-gray-500">
          {filtered.length} record{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4 text-sm">
          <i className="fas fa-exclamation-triangle mr-1"></i> <strong>Error:</strong> {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white p-4 rounded shadow">
        {loading ? (
          <p className="text-gray-500 text-center py-8">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-clipboard-list text-4xl mb-3"></i>
            <p>No inquiry records yet.</p>
            <button onClick={openCreate} className="mt-3 text-blue-600 hover:underline text-sm">
              Create the first one →
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Date', 'Visitor', 'Phone', 'Type', 'Child', 'Class', 'Status', 'Follow‑up', 'Actions'].map(h => (
                    <th key={h} className="p-3 text-left font-semibold text-gray-700 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => {
                  const typeMeta = INQUIRY_TYPES.find(t => t.value === row.inquiry_type) || INQUIRY_TYPES[0];
                  const statusMeta = STATUS_META[row.status] || STATUS_META.open;
                  return (
                    <tr key={row.id} className="border-t hover:bg-gray-50">
                      <td className="p-3 text-xs text-gray-500">
                        {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-3 font-medium">{row.visitor_name}</td>
                      <td className="p-3">{row.phone || '—'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${typeMeta.color}`}>
                          <i className={`fas ${typeMeta.icon} mr-1`}></i>{typeMeta.label}
                        </span>
                      </td>
                      <td className="p-3">{row.child_name || '—'}</td>
                      <td className="p-3 text-xs">{row.class_interested || '—'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${statusMeta.color}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="p-3 text-xs">{row.follow_up_date || '—'}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(row)} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200">
                            <i className="fas fa-edit"></i>
                          </button>
                          <button onClick={() => remove(row.id)} className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200">
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Inquiry modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setForm(EMPTY_FORM); }}
        title={editing ? 'Edit Inquiry' : 'New Admission / Inquiry'}
        onSave={save}
        saveLabel={saving ? 'Saving…' : (editing ? 'Update' : 'Record')}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Visitor Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Visitor Name <span className="text-red-500">*</span></label>
                <input className="w-full p-2 border rounded" placeholder="e.g. Ahmed Khan" value={form.visitor_name} onChange={e => setForm({ ...form, visitor_name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                <input className="w-full p-2 border rounded" placeholder="+92-300-1234567" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input className="w-full p-2 border rounded" placeholder="visitor@example.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
          </div>

          <hr className="border-gray-100" />

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Inquiry Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Inquiry Type</label>
                <select className="w-full p-2 border rounded" value={form.inquiry_type} onChange={e => setForm({ ...form, inquiry_type: e.target.value })}>
                  {INQUIRY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select className="w-full p-2 border rounded" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Student / Child Information <span className="ml-1 text-gray-400 font-normal normal-case">(optional)</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Child's Full Name</label>
                <input className="w-full p-2 border rounded" placeholder="e.g. Bilal Ahmed" value={form.child_name} onChange={e => setForm({ ...form, child_name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Class Interested In</label>
                <input className="w-full p-2 border rounded" placeholder="e.g. 5-A or Grade 1" value={form.class_interested} onChange={e => setForm({ ...form, class_interested: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Child's Date of Birth</label>
                <input type="date" className="w-full p-2 border rounded" value={form.child_dob} onChange={e => setForm({ ...form, child_dob: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Follow‑up Date <span className="ml-1 text-gray-400 font-normal">(when to contact again)</span>
                </label>
                <input type="date" className="w-full p-2 border rounded" value={form.follow_up_date} onChange={e => setForm({ ...form, follow_up_date: e.target.value })} />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea className="w-full p-2 border rounded" rows="3" placeholder="Any additional details about the visit, requirements, or concerns…" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
      </Modal>
    </div>
  );
}